import type { OptimizationJobCreateRequest } from '../../types/optimizationJob';
import type { OptimizationPreviewData, OptimizationPreviewRequest } from '../../types/optimizationPreview';
import { unifiedWebSocketService } from '../unifiedWebSocket';
import type { SingleAgentConfig } from '../unifiedWebSocket';
import { ApiResponse, OptimizationHistoryItem, OptimizationUsageData, Paginated } from '../../types/api';
import type {
  WorkflowEvaluationJob,
  WorkflowEvaluationJobList,
  WorkflowEvaluationJobRequest,
  WorkflowTuningChange,
  WorkflowTuningJob,
  WorkflowTuningJobList,
  WorkflowTuningJobRequest,
  WorkflowTuningJournal,
  WorkflowTuningPreview,
  WorkflowTuningProposal,
  WorkflowTuningUsage,
} from '../../types/workflowTuning';
import { api } from './client';

// AutoTune endpoints
export const autoTune = {
  optimize: async (data: {
    prompt: string;
    dataset_name: string | string[];
    model_name?: string;
    iterations?: number;
    temperature?: number;
  }) => {
    // Используем Unified WebSocket для оптимизации
    const config: SingleAgentConfig = {
      prompt: data.prompt,
      dataset_name: data.dataset_name,
      model_name: data.model_name,
      iterations: data.iterations,
      temperature: data.temperature,
    };

    return new Promise((resolve, reject) => {
      unifiedWebSocketService.optimize('single', config, {
        onComplete: (result) => resolve(result),
        onError: (error) => reject(error),
      });
    });
  },

  optimizeWithProgress: async (
    data: {
      prompt: string;
      dataset_name: string | string[];
      model_name?: string;
      iterations?: number;
      temperature?: number;
    },
    onProgress?: (progress: number) => void,
    onComplete?: (result: any) => void
  ) => {
    // Используем Unified WebSocket для оптимизации с прогрессом
    const config: SingleAgentConfig = {
      prompt: data.prompt,
      dataset_name: data.dataset_name,
      model_name: data.model_name,
      iterations: data.iterations,
      temperature: data.temperature,
    };

    return new Promise((resolve, reject) => {
      unifiedWebSocketService.optimize('single', config, {
        onProgress: onProgress,
        onComplete: (result) => {
          onComplete?.(result);
          resolve(result);
        },
        onError: (error) => reject(error),
      });
    });
  },

  chat: async (data: {
    temperature: number;
    prompt: string;
    dialog: Array<{ type: 'human' | 'ai'; content: string }>;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auto-tune/chat', data);
      return response.data;
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  },

  multiAgentChat: async (data: {
    model: {
      'master-agent': {
        name: string;
        prompt: string;
        model_name: string;
        temperature: number;
      };
      'worker-agents': Array<{
        name: string;
        prompt: string;
        model_name: string;
        temperature: number;
      }>;
    };
    dialog: Array<{ type: 'human' | 'ai'; content: string }>;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auto-tune/multiagent/chat', data);
      return response.data;
    } catch (error) {
      console.error('Multi-agent chat API error:', error);
      throw error;
    }
  },

  getDefaults: async (): Promise<ApiResponse> => {
    const response = await api.get<ApiResponse>('/auto-tune/defaults');
    return response.data;
  },

  // История оптимизаций (GET /auto-tune/optimize/history?limit=&offset=)
  getOptimizationHistory: async (params?: {
    limit?: number;
    offset?: number;
    run_id?: string;
  }): Promise<ApiResponse<Paginated<OptimizationHistoryItem>>> => {
    try {
      const response = await api.get<ApiResponse<Paginated<OptimizationHistoryItem>>>('/auto-tune/optimize/history', {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('🌐 getOptimizationHistory API error:', error);
      throw error;
    }
  },

  getOptimizationUsage: async (runId: string): Promise<ApiResponse<OptimizationUsageData>> => {
    try {
      const response = await api.get<ApiResponse<OptimizationUsageData>>(`/auto-tune/optimize/usage/${runId}`);
      return response.data;
    } catch (error) {
      console.error('🌐 getOptimizationUsage API error:', error);
      throw error;
    }
  },

  changeSelectedPrompt: async (data: { selected_prompt_id: number; run_id: string }): Promise<ApiResponse> => {
    const response = await api.put<ApiResponse>('/auto-tune/optimize/selected-prompt', data);
    return response.data;
  },

  saveToHistory: async (data: {
    run_id: string;
    initial_prompt: string;
    optimized_prompts: string[];
    model_name: string;
    dataset_name: string | string[];
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name: string;
      temperature: number;
    }>;
  }): Promise<ApiResponse> => {
    console.log('🌐 saveToHistory API call with data:', data);
    try {
      const response = await api.post<ApiResponse>('/auto-tune/optimize/history', data);
      console.log('🌐 saveToHistory API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('🌐 saveToHistory API error:', error);
      throw error;
    }
  },

  exportStep: async (data: {
    run_id: string;
    step_number: number;
  }): Promise<
    ApiResponse<{
      success: boolean;
      message: string;
      data: {
        id: number;
        config: string; // JSON string
        label: string;
        user_id: string | null;
        created_at: string;
        updated_at: string;
      };
    }>
  > => {
    try {
      const response = await api.post<
        ApiResponse<{
          success: boolean;
          message: string;
          data: {
            id: number;
            config: string;
            label: string;
            user_id: string | null;
            created_at: string;
            updated_at: string;
          };
        }>
      >('/auto-tune/optimize/export-step', data);
      return response.data;
    } catch (error) {
      console.error('🌐 exportStep API error:', error);
      throw error;
    }
  },

  /** Preview optimization: estimate token usage for a full run (ML predict_token_usage). */
  optimizePreview: async (payload: OptimizationPreviewRequest): Promise<ApiResponse<OptimizationPreviewData>> => {
    try {
      const response = await api.post<ApiResponse<OptimizationPreviewData>>('/auto-tune/optimize/preview', payload);
      return response.data;
    } catch (error) {
      console.error('🌐 optimizePreview API error:', error);
      throw error;
    }
  },

  createOptimizationJob: async (body: OptimizationJobCreateRequest): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auto-tune/optimize/jobs', body);
    return response.data;
  },

  getOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/auto-tune/optimize/jobs/${jobId}`);
    return response.data;
  },

  listOptimizationJobs: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<Paginated<any>>> => {
    const response = await api.get<ApiResponse<Paginated<any>>>('/auto-tune/optimize/jobs', { params });
    return response.data;
  },

  cancelOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/auto-tune/optimize/jobs/${jobId}/cancel`);
    return response.data;
  },

  /** Creates a new config tuning job from a failed/cancelled job; returns the new job payload. */
  retryOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/auto-tune/optimize/jobs/${encodeURIComponent(jobId)}/retry`);
    return response.data;
  },

  /** Preview evaluation job — estimate tokens/cost before starting (sync). */
  previewEvaluationJob: async (
    integrationId: string,
    body: import('../../types/evaluation').EvaluationRunRequestBody
  ): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJobPreviewData>> => {
    const response = await api.post<ApiResponse<import('../../types/evaluationJob').EvaluationJobPreviewData>>(
      '/auto-tune/evaluation/jobs/preview',
      body,
      {
        params: { integration_id: integrationId },
      }
    );
    return response.data;
  },

  createEvaluationJob: async (
    integrationId: string,
    body: import('../../types/evaluation').EvaluationRunRequestBody
  ): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../../types/evaluationJob').EvaluationJob>>(
      '/auto-tune/evaluation/jobs',
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  getEvaluationJob: async (jobId: string): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJob>> => {
    const response = await api.get<ApiResponse<import('../../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  listEvaluationJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJobListResponse>> => {
    const response = await api.get<ApiResponse<import('../../types/evaluationJob').EvaluationJobListResponse>>(
      '/auto-tune/evaluation/jobs',
      { params }
    );
    return response.data;
  },

  cancelEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<import('../../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  deleteEvaluationJob: async (jobId: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> => {
    const response = await api.delete<ApiResponse<{ id: string; deleted: boolean }>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  // ── Workflow tuning (AIT-138) ─────────────────────────────────────────────
  //
  // Sibling namespace of `/auto-tune/optimize/*`, not a mode of it: these tune the
  // target system's state graph. `integration_id` travels in the body on both
  // branches here, unlike plain evaluation which takes it in the query.

  createWorkflowTuningJob: async (body: WorkflowTuningJobRequest): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>('/auto-tune/workflow/jobs', body);
    return response.data;
  },

  /** Same body as `createWorkflowTuningJob` — the estimate is for the exact run that would start. */
  previewWorkflowTuningJob: async (body: WorkflowTuningJobRequest): Promise<ApiResponse<WorkflowTuningPreview>> => {
    const response = await api.post<ApiResponse<WorkflowTuningPreview>>('/auto-tune/workflow/jobs/preview', body);
    return response.data;
  },

  listWorkflowTuningJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkflowTuningJobList>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJobList>>('/auto-tune/workflow/jobs', { params });
    return response.data;
  },

  getWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  /** Only the changed paths — the diff UI is built from this, not from the full proposal. */
  getWorkflowTuningChanges: async (jobId: string): Promise<ApiResponse<WorkflowTuningChange[]>> => {
    const response = await api.get<ApiResponse<WorkflowTuningChange[]>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/changes`
    );
    return response.data;
  },

  /** The whole document, megabytes on a full graph — fetch only when applying. */
  getWorkflowTuningProposal: async (jobId: string): Promise<ApiResponse<WorkflowTuningProposal>> => {
    const response = await api.get<ApiResponse<WorkflowTuningProposal>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/proposal`
    );
    return response.data;
  },

  getWorkflowTuningJournal: async (jobId: string): Promise<ApiResponse<WorkflowTuningJournal>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJournal>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/journal`
    );
    return response.data;
  },

  getWorkflowTuningUsage: async (jobId: string): Promise<ApiResponse<WorkflowTuningUsage>> => {
    const response = await api.get<ApiResponse<WorkflowTuningUsage>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/usage`
    );
    return response.data;
  },

  cancelWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  // Workflow evaluation — measures the graph as it stands, improves nothing.

  createWorkflowEvaluationJob: async (
    body: WorkflowEvaluationJobRequest
  ): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>('/auto-tune/workflow/evaluation/jobs', body);
    return response.data;
  },

  listWorkflowEvaluationJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkflowEvaluationJobList>> => {
    const response = await api.get<ApiResponse<WorkflowEvaluationJobList>>('/auto-tune/workflow/evaluation/jobs', {
      params,
    });
    return response.data;
  },

  getWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.get<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  cancelWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  deleteWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> => {
    const response = await api.delete<ApiResponse<{ id: string; deleted: boolean }>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  // Pipeline scheduling
  createPipeline: async (
    body: import('../../types/pipeline').CreatePipelineRequest
  ): Promise<ApiResponse<import('../../types/pipeline').Pipeline>> => {
    const response = await api.post<ApiResponse<import('../../types/pipeline').Pipeline>>('/auto-tune/pipelines', body);
    return response.data;
  },

  listPipelines: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<import('../../types/pipeline').PipelineListResponse>> => {
    const response = await api.get<ApiResponse<import('../../types/pipeline').PipelineListResponse>>(
      '/auto-tune/pipelines',
      {
        params,
      }
    );
    return response.data;
  },

  getPipeline: async (pipelineId: string): Promise<ApiResponse<import('../../types/pipeline').Pipeline>> => {
    const response = await api.get<ApiResponse<import('../../types/pipeline').Pipeline>>(
      `/auto-tune/pipelines/${pipelineId}`
    );
    return response.data;
  },

  updatePipeline: async (
    pipelineId: string,
    body: import('../../types/pipeline').UpdatePipelineRequest
  ): Promise<ApiResponse<import('../../types/pipeline').Pipeline>> => {
    const response = await api.patch<ApiResponse<import('../../types/pipeline').Pipeline>>(
      `/auto-tune/pipelines/${pipelineId}`,
      body
    );
    return response.data;
  },

  deletePipeline: async (
    pipelineId: string
  ): Promise<ApiResponse<import('../../types/pipeline').DeletePipelineResponse>> => {
    const response = await api.delete<ApiResponse<import('../../types/pipeline').DeletePipelineResponse>>(
      `/auto-tune/pipelines/${pipelineId}`
    );
    return response.data;
  },

  startPipelineRun: async (pipelineId: string): Promise<ApiResponse<import('../../types/pipeline').PipelineRun>> => {
    const response = await api.post<ApiResponse<import('../../types/pipeline').PipelineRun>>(
      `/auto-tune/pipelines/${pipelineId}/runs`
    );
    return response.data;
  },

  listPipelineRuns: async (
    pipelineId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<Paginated<import('../../types/pipeline').PipelineRun>>> => {
    const response = await api.get<ApiResponse<Paginated<import('../../types/pipeline').PipelineRun>>>(
      `/auto-tune/pipelines/${pipelineId}/runs`,
      { params }
    );
    return response.data;
  },

  getPipelineRun: async (runId: string): Promise<ApiResponse<import('../../types/pipeline').PipelineRun>> => {
    const response = await api.get<ApiResponse<import('../../types/pipeline').PipelineRun>>(
      `/auto-tune/pipeline-runs/${runId}`
    );
    return response.data;
  },

  cancelPipelineRun: async (runId: string): Promise<ApiResponse<import('../../types/pipeline').PipelineRun>> => {
    const response = await api.post<ApiResponse<import('../../types/pipeline').PipelineRun>>(
      `/auto-tune/pipeline-runs/${runId}/cancel`
    );
    return response.data;
  },
};
